"use client";

import {
  CopilotAlert,
  useCopilotChat,
  type ToolComponentProps,
} from "@zapier/copilot-ui/client";

interface CheckSupportAvailabilityResult {
  available: boolean;
  reason?: string;
  message?: string;
}

/**
 * Component for check_support_availability server tool.
 * Shows a CopilotAlert when the user's email is not verified.
 * For other results (schedule unavailable, holiday, etc.), returns null
 * to let the AI handle the messaging.
 */
export const CheckSupportAvailabilityComponent = ({
  result,
}: ToolComponentProps<any, CheckSupportAvailabilityResult | string>) => {
  const { append } = useCopilotChat();

  // Parse the result if it's a string
  const parsedResult: CheckSupportAvailabilityResult | null =
    typeof result === "string" ? JSON.parse(result) : result;

  // Show verification alert if email is not verified
  if (parsedResult?.reason === "email_not_verified") {
    return (
      <CopilotAlert
        status="warning"
        title="Please verify your email"
        message="Before you can use Live Chat support, you'll need to verify your email address. Check your inbox for the verification email, or request a new one in your account settings. After your email is verified, click the Retry button below."
        actions={[
          {
            variant: "primary",
            children: "Go to Account Settings",
            href: "/app/settings/profile",
          },
          {
            variant: "secondary",
            children: "Retry",
            onPress: () => {
              append({ role: "user", content: "I want to chat with support" });
            },
          },
        ]}
      />
    );
  }

  // For other results (schedule unavailable, holiday, etc.),
  // return null to let the AI handle the messaging
  return null;
};
